import { z } from "zod";

/**
 * `POST /api/v1/ads-intelligence/insights/run` — single endpoint.
 *
 * **Structured payload (both `skill_id` values):** `StructuredThreeTabResponse`
 * — `critical`, `opportunities`, `wins`, optional `data_summary`. Use
 * `skill_id` only for copy/labels (`"insights"` vs `"recommendations"`), not
 * for branching the tab layout.
 *
 * For recommendations runs, `opportunities[].scope` may carry the canonical
 * UA lever token (e.g. creative, bidding) when the model used **[lever]**
 * markup.
 *
 * **`StructuredInsight` rows:** Prefer `issue`, `evidence`, `action`
 * (short strings, server-clamped ≤200 chars). Legacy payloads may omit them
 * and rely on `diagnosis`; card rails stay `entity_name`, `category`,
 * `platform`, `scope`.
 */

// ---------- platform / severity ----------

export const InsightsPlatformSchema = z.string();
export type InsightsPlatform =
  | "google"
  | "meta"
  | "appsflyer"
  | "cross-platform"
  | (string & {});

export const InsightsSeveritySchema = z.string();
/**
 * Severity: `critical` | `opportunity` | `win`. Legacy `warning` → treat as
 * `opportunity`. Legacy `amplify` in **severity** strings (old rows) → map
 * to `win` styling in the UI.
 */
export type InsightsSeverity =
  | "critical"
  | "opportunity"
  | "win"
  | "warning"
  | "amplify"
  | (string & {});

// ---------- card payload (single shape for all tabs) ----------

export const StructuredInsightSchema = z.object({
  severity: InsightsSeveritySchema,
  entity_name: z.string(),
  platform: InsightsPlatformSchema,
  /**
   * Sub-tag or, on recommendations runs, the canonical lever token
   * (creative, bidding, budget, …) when provided by extraction.
   */
  scope: z.string().nullable().optional().default(null),
  category: z.string(),
  /**
   * Optional legacy / extra prose. Omitted when the API only sends
   * `issue` / `evidence` / `action`.
   */
  diagnosis: z.string().optional().default(""),
  /** Labelled row — mirrors prompt lines `Issue:` (~≤25 words each; clamped ≤200 chars server-side). */
  issue: z.string().optional().default(""),
  evidence: z.string().optional().default(""),
  action: z.string().optional().default(""),
});
export type StructuredInsight = z.infer<typeof StructuredInsightSchema>;

const StructuredThreeTabFieldsSchema = z.object({
  critical: z.array(StructuredInsightSchema).default([]),
  opportunities: z.array(StructuredInsightSchema).default([]),
  wins: z.array(StructuredInsightSchema).default([]),
  data_summary: z.string().nullable().optional().default(null),
});

/**
 * Normalizes legacy API keys at the JSON layer: `warnings` → opportunities
 * when opportunities empty; `amplify` → wins when wins empty. Extra keys
 * (e.g. old `recommendations` lever objects) are dropped before parse.
 */
export const StructuredThreeTabSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== "object") return raw;
  const r = raw as Record<string, unknown>;
  const critical = Array.isArray(r.critical) ? r.critical : [];
  let opportunities = Array.isArray(r.opportunities) ? r.opportunities : [];
  let wins = Array.isArray(r.wins) ? r.wins : [];
  if (opportunities.length === 0 && Array.isArray(r.warnings)) {
    opportunities = r.warnings;
  }
  if (wins.length === 0 && Array.isArray(r.amplify)) {
    wins = r.amplify;
  }
  return {
    critical,
    opportunities,
    wins,
    data_summary: r.data_summary ?? null,
  };
}, StructuredThreeTabFieldsSchema);

export type StructuredThreeTabResponse = z.infer<typeof StructuredThreeTabFieldsSchema>;

// ---------- response envelope ----------

const RunEnvelopeSchema = z.object({
  skill_version: z.number(),
  content: z.string(),
  saved_prompt_id: z.string().nullable().optional().default(null),
  agent_used: z.string().nullable().optional().default(null),
  platforms_available: z.array(z.string()).default([]),
  elapsed_ms: z.number(),
  extraction_elapsed_ms: z.number().optional(),
  deep_research: z.boolean().default(false),
});

export const InsightsRunResponseSchema = z.discriminatedUnion("skill_id", [
  RunEnvelopeSchema.extend({
    skill_id: z.literal("insights"),
    structured: StructuredThreeTabSchema,
  }),
  RunEnvelopeSchema.extend({
    skill_id: z.literal("recommendations"),
    structured: StructuredThreeTabSchema,
  }),
]);
export type InsightsRunResponse = z.infer<typeof InsightsRunResponseSchema>;
export type InsightsRunInsightsResponse = Extract<InsightsRunResponse, { skill_id: "insights" }>;
export type InsightsRunRecommendationsResponse = Extract<
  InsightsRunResponse,
  { skill_id: "recommendations" }
>;

// ---------- request ----------

export const PROMPT_MAX = 4000;

export interface InsightsRunRequest {
  prompt: string;
  user_id?: string;
  organization_id?: string;
  customer_id?: string;
  google_customer_id?: string;
  currency?: string;
  today?: string;
  deep_research?: boolean;
  refresh?: boolean;
}

export const RUN_NO_SKILL_HINT = "did not load an output skill";
